import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, map, of, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { QuoterService } from './data/services/view/quoter.service';
import { ApiQuoterService } from './data/services/api/api-quoter.service';
import { UserRole } from '@data/enums/user-role.enum';
import { environment } from '@env/environment';

@Component({
  selector: 'app-quoter',
  templateUrl: './quoter.component.html',
  styleUrls: ['./quoter.component.scss'],
})
export class QuoterComponent {
  entity!: FormGroup;
  paymentTypes: { label: string; value: string; }[] = [
    { label: "Contado", value: "cash" },
    { label: "Financiado", value: "financed" }
  ]
  renewalTypes: { label: string; value: string; }[] = [
    { label: "Contado", value: "cash" },
    { label: "Financiado", value: "financed" }
  ];
  providers: { label: string; value: string; }[] = []
  quoteAmountsSubscription!: Subscription;
  quoteAmounts?: any = [{}] as any
  role: string = UserRole.BROKER
  // Expone el enum al template HTML para usar UserRole.BROKER en bindings
  public UserRole = UserRole
  assetsValue: number = 0
  loading: boolean = false
  user: string = '68114547033654b2aa008c97'
  createEntity: boolean = true

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public quoteService: QuoterService,
    private apiQuoteService: ApiQuoterService,
  ) { }

  ngOnInit() {
    this.entity = this.quoteService.entity;
    this.quoteAmountsSubscription = this.quoteService.quoteAmounts$.subscribe(
      (quoteAmounts) => {
        this.quoteAmounts = quoteAmounts;
      }
    );
  }

  ngOnDestroy() {
    if (this.quoteAmountsSubscription) {
      this.quoteAmountsSubscription.unsubscribe();
    }
  }

  get e() {
    return this.quoteService.entity
  }

  get accessories(): FormArray {
    return this.entity.get('accessories') as FormArray;
  }

  hasFinancedAccessory(): boolean {
    const accessories = this.quoteService.entity.get('accessories')?.value;
    return accessories ? accessories.some((accessory: any) => accessory.paymentType === 'financed') : false;
  }

  hasCashAccessory(): boolean {
    const accessories = this.quoteService.entity.get('accessories')?.value;
    return accessories ? accessories.some((accessory: any) => accessory.paymentType === 'cash') : false;
  }

  getPaymentType(value: string) {
    return this.paymentTypes.find(pt => pt.value === value)?.label
  }

  getRenewalType(value: string): string {
    const renewalType = this.renewalTypes.find(rt => rt.value === value);
    return renewalType ? renewalType.label : '';
  }

  getProviderName(id: string): string {
    const provider = this.providers.find(p => p.value === id);
    return provider ? provider.label : '';
  }

  addAccessory() {
    const accessoryForm = this.fb.group({
      name: ['', [Validators.required]],
      paymentType: ['', [Validators.required]],
      interestRate: [0],
      amount: [0, [Validators.required]],
      isInsurance: [false],
      insuranceDetails: this.fb.group({
        startPeriod: [],
        endPeriod: [],
        notifyBeforeExpiry: [0],
        provider: [''],
        policyNumber: [''],
        renewalType: [''],
      })
    });

    this.accessories.push(accessoryForm);
  }

  removeAccessory(index: number) {
    this.accessories.removeAt(index);
  }

  addAsset() {
    const assetName = this.quoteService.entity.get('assetInput')?.value;
    if (assetName) {
      const assetsArray = this.quoteService.entity.get('assets')?.value;
      assetsArray.push(assetName);
      this.quoteService.entity.get('assets')?.setValue(assetsArray);
      this.quoteService.entity.get('assetInput')?.setValue('');
    }
  }

  toggleInsuranceDetails(index: number) {
    const accessory = this.accessories.at(index) as FormGroup;
    const isInsurance = accessory.get('isInsurance')?.value;

    if (isInsurance) {
      accessory.get('insuranceDetails')?.enable();
    } else {
      accessory.get('insuranceDetails')?.disable();
    }
  }

  getProviders() {
    return this.http.post(environment.apiUrl + 'providers', { page: 1, limit: 10, filters: {} }).pipe(
      map((r) => r as { error: boolean, msg: string, data: any }),
      catchError((e) => of({ error: true, msg: 'Hubo un error de conexión', data: [] }))
    )
  }

  getRenewalLabel(accessory: AbstractControl): string | undefined {
    const renewalTypeValue = accessory.get('renewalConfig')?.get('renewalType')?.value;
    return this.renewalTypes.find(rt => rt.value === renewalTypeValue)?.label;
  }

  getFormControl(group: FormGroup, controlName: string): FormControl {
    return group.get(controlName) as FormControl;
  }

  onTermsChange(selectedTerms: number[]) {
    const termsArray: any[] = this.quoteService.entity.get('terms')?.value || [];

    // Agregar términos nuevos
    selectedTerms.forEach(term => {
      if (!termsArray.some(t => t.term === term)) {
        termsArray.push({ term, residuary: 0 });
      }
    });

    // Remover términos deseleccionados
    const updatedTerms = termsArray.filter(t => selectedTerms.includes(t.term));

    // Actualizar el control del formulario
    this.quoteService.entity.get('terms')?.setValue(updatedTerms);
  }

  onAssetsChange(event: any) {
    const selectedAssetIds = event.value;
    this.assetsValue = 0;

    selectedAssetIds.forEach((assetId: string) => {
      const asset = this.quoteService.actives.find(a => a.value === assetId);
      if (asset) {
        this.assetsValue += asset.amount;
      }
    });
  }

  saveForm() {
    const data = { ...this.quoteService.entity.value };
    data.user = '68114547033654b2aa008c97'
    data.case = this.quoteService.formMethod
    data.terms = this.quoteService.termsArray.value.filter((t: any) => this.quoteService.isTermAllowed(t.term))

    if (data.accessories.length > 0) {
      data.accessories.forEach((accessory: any) => {
        if (accessory.insuranceDetails) {
          if (accessory.insuranceDetails.startPeriod !== null && accessory.insuranceDetails.endPeriod !== null) {
            accessory.insuranceDetails.startPeriod = new Date(accessory.insuranceDetails.startPeriod);
            accessory.insuranceDetails.endPeriod = new Date(accessory.insuranceDetails.endPeriod);
          }
        }
        if (accessory.isInsurance === false) {
          accessory.insuranceDetails = null;
        }
      });
    } else {
      data.accessories = [];
    }

    if (data.case === 'CREATE' || data.case === 'UNSAVE') {
      data.case = 'CREATE'
      this.apiQuoteService.create(data).subscribe(
        (response) => {
          this.loading = false;
        },
        (error) => {
          console.error('Error saving form:', error);
        }
      );
    }

    this.quoteService.updateQuoteAmounts([{
      term: 0,
      irr: 0,
      advance: 0,
      openingCommission: 0,
      advancedRents: 0,
      ratificationExpenses: 0,
      accessories: [],
      advanceSubtotal: 0,
      advanceIva: 0,
      advanceTotal: 0,
      monthlyCapital: 0,
      monthlyInterest: 0,
      monthlyAccesories: [],
      monthlySubtotal: 0,
      monthlyIva: 0,
      monthlyTotal: 0,
      residuary: 0,
      residuaryIva: 0,
      residuaryTotal: 0,
    }])
  }

    getAmounts() {
    this.loading = true;
    const data = { ...this.quoteService.entity.value };
    data.user = this.user;
    delete data.id;
    data.terms = this.quoteService.termsArray.value.filter((t: any) => this.quoteService.isTermAllowed(t.term))

    if (data.accessories.length > 0) {
      data.accessories.forEach((accessory: any) => {
        if (accessory.insuranceDetails) {
          if (accessory.insuranceDetails.startPeriod !== null && accessory.insuranceDetails.endPeriod !== null) {
            accessory.insuranceDetails.startPeriod = new Date(accessory.insuranceDetails.startPeriod);
            accessory.insuranceDetails.endPeriod = new Date(accessory.insuranceDetails.endPeriod);
          }
        }
        if (accessory.isInsurance === false) {
          accessory.insuranceDetails = null;
        }
      });
    } else {
      data.accessories = [];
    }

    this.apiQuoteService.getAmounts(data).subscribe(
      (response) => {
        this.loading = false;
        this.quoteService.updateQuoteAmounts(response.data);
      },
      (error) => {
        console.error('Error saving form:', error);
        this.loading = false;
      }
    )

    this.loading = false
  }
}
